/// <amd-module name="Dashboards/WidgetHost/OverlayComponents/WidgetHostOverlay" />
import * as Resources from "Dashboards/WidgetHost/Resources";
import React = require("react");
import { css, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { IVssContextualMenuItem } from "VSSUI/Components/VssContextualMenu";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { IMenuItem, ContextualMenu } from "VSSUI/Menu";
import { Location } from "VSSUI/Utilities/Position";

/**
 * WidgetHost Overlay is a shallow control, which passes simple non-contextual events up to the Widget Host, which exposes proper events for consumers.
 */
export interface WidgetHostOverlayProps extends IVssComponentProperties {
    isPointerInsideParent: boolean;
    allowLightbox?: boolean;
    canConfigureWidget?: boolean;
    canCopyWidget?: boolean;
    canDeleteWidget?: boolean;
    canViewWidgetInformation?: boolean;

    /**
     * Indicates if this overlay should use dark colors over the  widget.
     */
    useDarkOverlay: boolean;

    // Custom Menu items explicitly supplied by the Widget
    customMenuItems?: IVssContextualMenuItem[];

    // Note: Lightbox is handled directly by Widget Host
    onLightbox?: () => void;

    // Note: Copy UI is presented by Widget Host, but experience handles the data processing (e.g. REST call vs in-page experience in dashboards)
    onCopyToDashboard?: (moreActionsButton: Button | null) => void;

    // Configure is responsibility of Experience
    onConfigureWidget?: () => void;
    // Delete is responsibility of Experience
    onDeleteWidget?: () => void;
    onViewWidgetInformation?: () => void;

    // Indicates if config button of the widget is to be focused by default.
    shouldFocusMoreActionsButtonByDefault?: boolean;
}

interface WidgetHostOverlayState {
    isContextMenuVisible: boolean;
    // The control which anchors the context menu.
    target?: any;
}

/**
 * Responsible for rendering overlay experience.
 *      -Provides context menu, edit/delete and lightbox options for widgets which have pertinent interactivity options enabled.
 */
export class WidgetHostOverlay extends VssComponent<WidgetHostOverlayProps, WidgetHostOverlayState> {
    public constructor(props: WidgetHostOverlayProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            isContextMenuVisible: false,
            target: null
        };
    }

    public render(): JSX.Element {
        return <FocusWithin>{this.renderOverlay}</FocusWithin>;
    }

    public componentDidMount() {
        this.focusMoreActionsButton();
    }

    public componentDidUpdate() {
        this.focusMoreActionsButton();
    }

    private renderOverlay = (focusStatus: IFocusWithinStatus): JSX.Element => {

        // map IVssContextualMenuItem to IMenuItem
        const menuItems: IMenuItem[] = this.getMenuItems().map((item) => {
            return {
                id: item.key,
                onActivate: item.onClick,
                text: item.name,
                iconProps: item.iconProps
            } as IMenuItem;
        });
        return (
            <div className="widget-host-overlay">
                <div
                    className={css(
                        "widget-host-menu-header",
                        "inline-flex-row",
                        this.props.isPointerInsideParent && "mouse-is-over-widget",
                        this.state.isContextMenuVisible && "context-menu-is-active",
                        focusStatus.hasFocus && "overlay-has-focus",
                        this.props.useDarkOverlay && "button-is-dark"
                    )}
                >
                    {this.props.allowLightbox && this.props.onLightbox && (
                        <Button
                            className="lightbox-widget-button"
                            iconProps={{ iconName: "FullScreen" }}
                            onClick={this.props.onLightbox}
                            onFocus={focusStatus.onFocus}
                            onBlur={focusStatus.onBlur}
                            ariaLabel={Resources.LightboxAriaLabel}
                            tooltipProps={{ text: Resources.LightboxTitle }}
                        />
                    )}
                    {this.state.isContextMenuVisible && this.renderMenu(menuItems)}
                    {menuItems.length > 0 && (
                        <Button
                            className="configure-widget-button"
                            iconProps={{ iconName: "More" }}
                            ref={(button) => { this._moreActionsButton = button }}
                            onClick={this.onShowMenu}
                            onFocus={focusStatus.onFocus}
                            onBlur={focusStatus.onBlur}
                            tooltipProps={{ text: Resources.MoreActionsButtonText }}
                            ariaLabel={Resources.MoreActionsButtonText}
                            ariaHasPopup={true}
                            ariaExpanded={this.state.isContextMenuVisible}
                        />
                    )}
                </div>
            </div>
        );
    };

    private renderMenu = (items: IMenuItem[]): JSX.Element => {
        return (
            <ContextualMenu 
                anchorElement={this.state.target}
                anchorOrigin={{ horizontal: Location.end, vertical: Location.end }}
                menuOrigin={{ horizontal: Location.end, vertical: Location.start }}
                menuProps={{
                    id:"widget-host-overlay-menu",
                    items: items,
                }}
                onDismiss={this.onDismiss}
            />
        )
    };

    private focusMoreActionsButton() {
        if (this._moreActionsButton && this.props.shouldFocusMoreActionsButtonByDefault) {
            this._moreActionsButton.focus();
        }
    }

    private getMenuItems = (): IVssContextualMenuItem[] => {
        let items: IVssContextualMenuItem[] = [];
        if (this.props.canConfigureWidget && this.props.onConfigureWidget) {
            items.push({
                key: "Configure",
                iconProps: { iconName: "Settings" },
                name: Resources.ConfigureWidgetText,
                onClick: this.props.onConfigureWidget
            });
        }
        if (this.props.canCopyWidget && this.props.onCopyToDashboard) {
            items.push({
                key: "Copy",
                iconProps: { iconName: "Add" },
                name: Resources.CopyToDashboardText,
                onClick: this._onCopyToDashboardClicked
            });
        }

        if (this.props.customMenuItems && Array.isArray(this.props.customMenuItems)) {
            items = items.concat(this.props.customMenuItems);
        }

        if (this.props.canViewWidgetInformation && this.props.onViewWidgetInformation) {
            items.push({
                key: "Info",
                iconProps: { iconName: "Info" },
                name: Resources.AboutThisWidgetText,
                onClick: this.props.onViewWidgetInformation
            });
        }
        if (this.props.canDeleteWidget && this.props.onDeleteWidget) {
            items.push({
                key: "Delete",
                iconProps: {
                    iconName: "Cancel",
                    className: "widget-host-delete-menu-icon"
                },
                name: Resources.DeleteWidgetText,
                onClick: this.props.onDeleteWidget
            });
        }
        return items;
    };

    private onShowMenu = (event: React.SyntheticEvent<HTMLElement>): void => {
        this.setState({
            isContextMenuVisible: true,
            target: event.target
        });
    };

    private onDismiss = (): void => {
        this.setState({ isContextMenuVisible: false });
    };

    private _onCopyToDashboardClicked = (): void => {
        if (this.props.onCopyToDashboard) {
            this.props.onCopyToDashboard(this._moreActionsButton);
        }
    }

    private _moreActionsButton: Button | null;
}
